import { useMutation, useQuery } from "@tanstack/react-query";
import { checkoutService } from "../services/checkoutService";
import { useCheckoutStore } from "../store/useCheckoutStore";
import { spinnerStore } from "../context/spinnerStore";
import { statusModalStore } from "../context/statusModalStore";

const HAS_RESERVATION = "kiosk::has::reservation";

export const useGetConsecutive = () => {
  const setConsecutive = useCheckoutStore((state) => state.setConsecutive);
  const showSpinner = spinnerStore((state) => state.showSpinner);
  const hideSpinner = spinnerStore((state) => state.hideSpinner);

  return useQuery({
    queryKey: ["consecutive"],
    queryFn: async () => {
      try {
        showSpinner();
        const response = await checkoutService.getConsecutive();
        const { consecutive } = response.data;
        if (consecutive) {
          hideSpinner();
          setConsecutive(consecutive);
          return consecutive;
        }
      } catch (error) {
        console.error(error);
        hideSpinner();
      }
    },
  });
};

export const useGetShowtimeData = (showtimeId) => {
  const setBookingDetails = useCheckoutStore(
    (state) => state.setBookingDetails
  );
  const showSpinner = spinnerStore((state) => state.showSpinner);
  const hideSpinner = spinnerStore((state) => state.hideSpinner);

  return useQuery({
    queryKey: ["ShowtimeData"],
    queryFn: async () => {
      try {
        showSpinner();
        const response = await checkoutService.getShowtimeData(showtimeId);
        await setBookingDetails(response.data);
        hideSpinner();
        return response.data;
      } catch (error) {
        console.error(error);
        hideSpinner();
      }
    },
  });
};

export const useGetShowtimeStatus = (showtimeId) => {
  const setBookingDetails = useCheckoutStore(
    (state) => state.setBookingDetails
  );
  const showSpinner = spinnerStore((state) => state.showSpinner);
  const hideSpinner = spinnerStore((state) => state.hideSpinner);

  return useQuery({
    queryKey: ["ShowtimeStatus", showtimeId],
    queryFn: async () => {
      try {
        showSpinner();
        const response = await checkoutService.getShowtimeStatus(showtimeId);
        setBookingDetails(response.data);
        hideSpinner();
        return response.data;
      } catch (error) {
        console.error(error);
        hideSpinner();
      }
    },
    enabled: false,
  });
};

export const useGetPayBrands = () => {
  const setPayOption = useCheckoutStore((state) => state.setPayOption);
  const showSpinner = spinnerStore((state) => state.showSpinner);
  const hideSpinner = spinnerStore((state) => state.hideSpinner);

  return useQuery({
    queryKey: ["payBrands"],
    queryFn: async () => {
      try {
        showSpinner();
        const response = await checkoutService.getPayBrands();
        setPayOption(response.data.data);
        hideSpinner();
        return response.data.data;
      } catch (error) {
        console.error(error);
      }
    },
  });
};

export const useGetConfectioneries = () => {
  const setConfectioneries = useCheckoutStore(
    (state) => state.setConfectioneries
  );
  const showSpinner = spinnerStore((state) => state.showSpinner);
  const hideSpinner = spinnerStore((state) => state.hideSpinner);

  return useQuery({
    queryKey: ["confectioneries"],
    queryFn: async () => {
      try {
        showSpinner();
        const response = await checkoutService.getConfectioneries();
        const {
          data: { data: Lista_Productos },
        } = response;
        setConfectioneries(Lista_Productos);
        hideSpinner();
        return Lista_Productos;
      } catch (error) {
        hideSpinner();
        console.error(error);
      }
    },
  });
};

export const useGetConfectioneriesApi = () => {
  const setConfectioneries = useCheckoutStore(
    (state) => state.setConfectioneriesApi
  );
  const showSpinner = spinnerStore((state) => state.showSpinner);
  const hideSpinner = spinnerStore((state) => state.hideSpinner);

  return useQuery({
    queryKey: ["confectioneriesApi"],
    queryFn: async () => {
      try {
        showSpinner();
        const response = await checkoutService.getConfectioneriesApi();
        const {
          data: { data },
        } = response;
        setConfectioneries(data);
        hideSpinner();
        return response;
      } catch (error) {
        hideSpinner();
        console.error(error);
      }
    },
  });
};

export const useReserveShowtime = () => {
  const showSpinner = spinnerStore((state) => state.showSpinner);
  const hideSpinner = spinnerStore((state) => state.hideSpinner);

  const {
    consecutive,
    selectedSeats,
    setSelectedSeats,
    bookingDetails: { showtime },
  } = useCheckoutStore.getState();

  const roomStatus = useGetShowtimeStatus(showtime.id);
  const handleErrorReserve = () => {
    setSelectedSeats([]);
    roomStatus.refetch();
    localStorage.removeItem(HAS_RESERVATION);
  };

  const data = {
    score_consecutive: consecutive?.Secuencia,
    seats: selectedSeats,
    device: "kiosk", // web, app, kiosk
  };
  return useMutation({
    mutationKey: ["ReserveShowtime"],
    mutationFn: async (showtimeId) =>
      await checkoutService.reserve(showtimeId, data),
    onMutate: () => {
      showSpinner("Reservando sillas...");
    },
    // eslint-disable-next-line no-unused-vars
    onSuccess: (response) => {
      localStorage.setItem(HAS_RESERVATION, "true");
    },
    onError: (error) => {
      console.error("Error al reservar:", error);
      statusModalStore.getState().showModal({
        title: "Estas sillas ya están reservadas",
        subtitle: "Por favor, elige otro asiento.",
        statusType: "warning", // o "success" | "error"
        actionText: "Ok",
        action: () => handleErrorReserve(),
      });
    },
    onSettled: () => {
      hideSpinner();
    },
  });
};

export const useCancelReserveShowtime = () => {
  const showSpinner = spinnerStore((state) => state.showSpinner);
  const hideSpinner = spinnerStore((state) => state.hideSpinner);

  const { consecutive } = useCheckoutStore.getState();

  const data = {
    score_consecutive: consecutive?.Secuencia,
    device: "kiosk", // web, app, kiosk
  };

  return useMutation({
    mutationKey: ["CalcelReserveShowtime"],
    mutationFn: async (showtimeId) => {
      if (localStorage.getItem(HAS_RESERVATION)) {
        await checkoutService.cancelReserve(showtimeId, data);
      }
    },
    onMutate: () => {
      showSpinner();
    },
    // eslint-disable-next-line no-unused-vars
    onSuccess: (response) => {
      localStorage.removeItem(HAS_RESERVATION);
    },
    onError: (error) => {
      console.error("Error al cancelar reserva:", error);
    },
    onSettled: () => {
      hideSpinner();
    },
  });
};
